/**
 * Styles - Generates CSS for voice interface
 * 
 * Required from parent (voiceInterface):
 * - config: { avatarBackgroundColor, statusTitleColor, ... }
 * - config.useShadowDOM: boolean
 * 
 * State owned by this module:
 * - None (stateless CSS generation)
 */

export class Styles {
  constructor(voiceInterface) {
    this.voiceInterface = voiceInterface;
    this.config = voiceInterface.config;
  }

  /**
   * Generate CSS for voice interface - Optimized for small heights
   */
  generateCSS() {
    const important = ' !important';
    const c = this.config;

    const heroGrad1 = c.heroGradient1 || '#2a2550';
    const heroGrad2 = c.heroGradient2 || '#1a1a2e';
    const avatarGrad1 = c.avatarGradient1 || '#6d56f5';
    const avatarGrad2 = c.avatarGradient2 || '#a78bfa';
    const onlineDotColor = c.onlineDotColor || '#22c55e';
    const agentNameColor = c.agentNameColor || '#f0eff8';
    const agentRoleColor = c.agentRoleColor || 'rgba(255,255,255,0.35)';
    const headlineColor = c.headlineColor || '#ffffff';
    const sublineColor = c.sublineColor || 'rgba(255,255,255,0.45)';
    const primaryGrad1 = c.primaryBtnGradient1 || c.startCallButtonColor || '#6d56f5';
    const primaryGrad2 = c.primaryBtnGradient2 || c.startCallButtonColor || '#9d8df8';
    const talkToPcFooterLink = c._talkToPcLinkColor || primaryGrad1;
    const primaryTextColor = c.startCallButtonTextColor || '#fff';
    const secondaryBg = c.secondaryBtnBg || 'rgba(255,255,255,0.05)';
    const secondaryBorder = c.secondaryBtnBorder || 'rgba(255,255,255,0.09)';
    const secondaryTextColor = c.secondaryBtnTextColor || 'rgba(255,255,255,0.6)';
    const panelBg = c.panelBackgroundColor || '#16161e';
    const endCallColor = c.endCallButtonColor || '#ef4444';
    const sendBtnColor = c.sendButtonColor || primaryGrad1;
    const callSurfaceGradient = `linear-gradient(160deg, ${heroGrad1} 0%, ${heroGrad2} 100%)`;

    return `
      /* Voice Interface - Dark Theme Desktop */
      .voice-interface {
        display: none${important};
        flex: 1${important};
        flex-direction: column${important};
        align-items: stretch${important};
        justify-content: flex-start${important};
        padding: 0${important};
        background: ${panelBg};
        overflow: hidden${important};
        min-height: 0${important};
        height: 100%${important};
        width: 100%${important};
        box-sizing: border-box${important};
      }
      .voice-interface.active {
        display: flex${important};
        background: ${callSurfaceGradient}${important};
      }

      .voice-interface.active:has(#voiceIdleState:not([style*="display: none"]):not([style*="display:none"])) {
        background: transparent${important};
      }

      /* Footer: same in-call purple as voice area (parent also sets bg; this covers any layout gaps) */
      .voice-interface.active ~ .ttp-footer {
        background: ${callSurfaceGradient}${important};
        border-top-color: rgba(255,255,255,0.06)${important};
      }
      .voice-interface.active ~ .ttp-footer span { color: rgba(255,255,255,0.35)${important}; }
      .voice-interface.active ~ .ttp-footer a { color: ${talkToPcFooterLink}${important}; }

      #voiceIdleState {
        display: flex${important};
        flex-direction: column${important};
        flex: 1${important};
        min-height: 0${important};
        width: 100%${important};
        box-sizing: border-box${important};
        background: transparent${important};
      }
      #voiceIdleState[style*="display: none"],
      #voiceIdleState[style*="display:none"] {
        display: none !important;
      }

      #voiceActiveState {
        display: flex${important};
        flex-direction: column${important};
        width: 100%${important};
        height: 100%${important};
        min-height: 0${important};
        overflow: hidden${important};
        flex: 1${important};
        box-sizing: border-box${important};
      }
      #voiceActiveState[style*="display: none"],
      #voiceActiveState[style*="display:none"] {
        display: none !important;
      }

      /* ── IDLE HERO — transparent; gradient is on #voiceIdleState so hero + body share one surface ── */
      .ttp-hero {
        position: relative${important};
        padding: 24px 22px 22px${important};
        background: transparent${important};
        border-bottom: none${important};
        overflow: hidden${important};
      }
      .ttp-hero::after {
        content: ''${important};
        position: absolute${important};
        top: -40px${important}; right: -40px${important};
        width: 140px${important}; height: 140px${important};
        border-radius: 50%${important};
        background: radial-gradient(circle, ${primaryGrad1}2e 0%, transparent 70%);
        pointer-events: none${important};
      }
      .ttp-header {
        display: flex${important};
        align-items: center${important};
        justify-content: space-between${important};
        margin-bottom: 20px${important};
        position: relative${important};
        z-index: 1${important};
      }
      .ttp-agent { display: flex${important}; align-items: center${important}; gap: 10px${important}; }
      .ttp-avatar-wrap { position: relative${important}; }
      .ttp-avatar {
        width: 36px${important}; height: 36px${important}; border-radius: 50%${important};
        background: linear-gradient(135deg, ${avatarGrad1}, ${avatarGrad2});
        display: flex${important}; align-items: center${important}; justify-content: center${important};
        font-size: 14px${important}; font-weight: 500${important}; color: #fff${important};
      }
      .ttp-avatar.ttp-avatar--image {
        padding: 0${important};
        overflow: hidden${important};
      }
      .ttp-avatar-img {
        width: 100%${important};
        height: 100%${important};
        object-fit: cover${important};
        display: block${important};
        border-radius: 50%${important};
      }
      .ttp-online-dot {
        position: absolute${important}; bottom: 0${important}; right: 0${important};
        width: 10px${important}; height: 10px${important}; border-radius: 50%${important};
        background: ${onlineDotColor}; border: 2px solid ${heroGrad2};
      }
      .ttp-agent-name { font-size: 14px${important}; font-weight: 500${important}; color: ${agentNameColor}; }
      .ttp-agent-role { font-size: 10px${important}; color: ${agentRoleColor}; margin-top: 1px${important}; }
      .ttp-close-btn {
        width: 26px${important}; height: 26px${important}; border-radius: 50%${important};
        background: rgba(255,255,255,0.07)${important}; border: none${important}; cursor: pointer${important};
        color: rgba(255,255,255,0.4)${important};
        display: flex${important}; align-items: center${important}; justify-content: center${important};
        transition: background 0.2s${important};
      }
      .ttp-close-btn:hover { background: rgba(255,255,255,0.14)${important}; }
      .ttp-headline {
        font-size: 22px${important}; font-weight: 500${important}; color: ${headlineColor};
        line-height: 1.2${important}; margin-bottom: 6px${important}; position: relative${important}; z-index: 1${important};
      }
      .ttp-subline {
        font-size: 13px${important}; color: ${sublineColor};
        line-height: 1.5${important}; position: relative${important}; z-index: 1${important};
      }

      /* ── DOMAIN ERROR CARD ── */
      .ttp-domain-error-card {
        display: flex${important};
        align-items: flex-start${important};
        gap: 12px${important};
        padding: 14px 16px${important};
        border-radius: 12px${important};
        background: rgba(239,68,68,0.08)${important};
        border: 1px solid rgba(239,68,68,0.18)${important};
      }
      .ttp-domain-error-icon {
        flex-shrink: 0${important};
        margin-top: 1px${important};
      }
      .ttp-domain-error-text {
        font-size: 12px${important};
        line-height: 1.55${important};
        color: rgba(252,165,165,0.85)${important};
      }
      .ttp-domain-error-text strong {
        color: #fca5a5${important};
        font-weight: 600${important};
      }

      /* ── IDLE BODY ── */
      .ttp-body {
        padding: 10px 18px 14px${important};
        display: flex${important}; flex-direction: column${important}; gap: 6px${important};
      }
      .ttp-btn-primary {
        width: 100%${important}; padding: 11px${important}; border-radius: 12px${important};
        background: linear-gradient(135deg, ${primaryGrad1}, ${primaryGrad2});
        border: none${important}; cursor: pointer${important};
        font-size: 13px${important}; font-weight: 500${important}; color: ${primaryTextColor};
        display: flex${important}; align-items: center${important}; justify-content: center${important}; gap: 8px${important};
        box-shadow: 0 4px 18px ${primaryGrad1}59;
        transition: opacity 0.2s, transform 0.15s${important};
        font-family: inherit${important};
      }
      .ttp-btn-primary:hover { opacity: 0.9${important}; transform: translateY(-1px)${important}; }
      .ttp-btn-primary:disabled { cursor: not-allowed${important}; opacity: 0.6${important}; }
      .ttp-btn-secondary {
        width: 100%${important}; padding: 10px${important}; border-radius: 12px${important};
        background: ${secondaryBg};
        border: 1px solid ${secondaryBorder};
        cursor: pointer${important}; font-size: 13px${important}; color: ${secondaryTextColor};
        display: flex${important}; align-items: center${important}; justify-content: center${important}; gap: 8px${important};
        transition: background 0.2s${important}; font-family: inherit${important};
      }
      .ttp-btn-secondary:hover { background: ${secondaryBg}; }

      /* WhatsApp button */
      .ttp-btn-wa {
        width: 100%${important}; padding: 10px${important}; border-radius: 12px${important};
        background: rgba(37,211,102,0.08)${important};
        border: 1px solid rgba(37,211,102,0.2)${important};
        cursor: pointer${important}; font-size: 13px${important}; font-weight: 500${important}; color: #4ade80${important};
        display: flex${important}; align-items: center${important}; justify-content: center${important}; gap: 8px${important};
        transition: background 0.2s${important}; font-family: inherit${important};
        text-decoration: none${important};
        box-sizing: border-box${important};
      }
      .ttp-btn-wa:hover { background: rgba(37,211,102,0.14)${important}; }

      /* Connecting Spinner (reused from old design) */
      .connecting-spinner {
        display: inline-block${important};
        width: 16px${important}; height: 16px${important};
        border: 2px solid rgba(255,255,255,0.3)${important};
        border-top-color: #fff${important};
        border-radius: 50%${important};
        animation: ttp-spin 1s linear infinite${important};
        margin-right: 8px${important};
        vertical-align: middle${important};
      }
      @keyframes ttp-spin { to { transform: rotate(360deg); } }

      /* ── ACTIVE CALL ── */
      .ttp-call-hero {
        background: transparent${important};
        padding: 22px 20px 18px${important};
        border-bottom: 1px solid rgba(255,255,255,0.06)${important};
      }
      .ttp-call-topbar {
        display: flex${important}; align-items: center${important}; justify-content: space-between${important};
        margin-bottom: 18px${important};
      }
      .ttp-rec { display: flex${important}; align-items: center${important}; gap: 7px${important}; }
      .ttp-rec-dot {
        width: 7px${important}; height: 7px${important}; border-radius: 50%${important}; background: #f87171${important};
        animation: ttp-pulse 1s ease-in-out infinite${important};
      }
      .ttp-rec-dot.paused {
        background: #f59e0b${important};
        animation: none${important};
      }
      ${this.config.useShadowDOM === false ? `
      /* Regular DOM (e.g. Shopify): theme rules often strip span/div sizing — recording dot + pulse */
      #ttp-widget-container .ttp-call-topbar .ttp-rec,
      #text-chat-panel .ttp-call-topbar .ttp-rec {
        display: flex !important;
        align-items: center !important;
        gap: 7px !important;
        flex-shrink: 0 !important;
      }
      #ttp-widget-container .ttp-rec-dot,
      #ttp-widget-container .compact-timer .ttp-rec-dot,
      #text-chat-panel .ttp-rec-dot,
      #text-chat-panel .compact-timer .ttp-rec-dot {
        display: block !important;
        flex-shrink: 0 !important;
        box-sizing: content-box !important;
        width: 7px !important;
        height: 7px !important;
        min-width: 7px !important;
        min-height: 7px !important;
        max-width: none !important;
        max-height: none !important;
        padding: 0 !important;
        margin: 0 !important;
        border: none !important;
        border-radius: 50% !important;
        background: #f87171 !important;
        background-color: #f87171 !important;
        opacity: 1 !important;
        visibility: visible !important;
        -webkit-animation: ttp-pulse 1s ease-in-out infinite !important;
        animation: ttp-pulse 1s ease-in-out infinite !important;
      }
      #ttp-widget-container .ttp-transcript-hdr .ttp-dot,
      #text-chat-panel .ttp-transcript-hdr .ttp-dot {
        display: flex !important;
        width: 3px !important;
        height: 3px !important;
        min-width: 3px !important;
        min-height: 3px !important;
        border-radius: 50% !important;
        background: ${avatarGrad2} !important;
        background-color: ${avatarGrad2} !important;
        opacity: 1 !important;
        visibility: visible !important;
        -webkit-animation: ttp-pulse 0.8s ease-in-out infinite !important;
        animation: ttp-pulse 0.8s ease-in-out infinite !important;
      }
      /* Orb waveform: same motion as shadow DOM (ttp-wave keyframes). Do not set opacity !important
         on bars — it overrides keyframe opacity and changes the look. */
      #ttp-widget-container .ttp-wave,
      #text-chat-panel .ttp-wave {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        gap: 3px !important;
        height: 26px !important;
        min-height: 26px !important;
        width: auto !important;
        min-width: 36px !important;
        overflow: visible !important;
        flex-shrink: 0 !important;
      }
      #ttp-widget-container .ttp-wave-bar,
      #text-chat-panel .ttp-wave-bar {
        display: block !important;
        box-sizing: content-box !important;
        width: 2.5px !important;
        min-width: 2.5px !important;
        max-width: none !important;
        flex-shrink: 0 !important;
        border-radius: 3px !important;
        background: rgba(255, 255, 255, 0.9) !important;
        background-color: rgba(255, 255, 255, 0.9) !important;
        visibility: visible !important;
        transform-origin: center center !important;
        -webkit-animation: ttp-wave 0.75s ease-in-out infinite alternate !important;
        animation: ttp-wave 0.75s ease-in-out infinite alternate !important;
      }
      #ttp-widget-container .ttp-orb,
      #text-chat-panel .ttp-orb {
        overflow: visible !important;
      }
      ` : ''}
      .ttp-timer { font-size: 11px${important}; color: rgba(255,255,255,0.4)${important}; letter-spacing: 0.05em${important}; }
      .ttp-call-label { font-size: 11px${important}; color: rgba(255,255,255,0.25)${important}; }

      /* ── ORB ── */
      .ttp-orb-wrap {
        display: flex${important}; flex-direction: column${important}; align-items: center${important};
        gap: 12px${important}; padding: 8px 0${important};
      }
      .ttp-orb-rings {
        position: relative${important}; width: 72px${important}; height: 72px${important};
        display: flex${important}; align-items: center${important}; justify-content: center${important};
      }
      .ttp-ring {
        position: absolute${important}; border-radius: 50%${important};
        border: 1.5px solid ${primaryGrad1}33;
        animation: ttp-ring 2s ease-in-out infinite${important};
      }
      .ttp-ring-1 { inset: -8px${important}; }
      .ttp-ring-2 { inset: -16px${important}; border-width: 1px${important}; border-color: ${primaryGrad1}1a; animation-delay: 0.5s${important}; }
      .ttp-orb {
        width: 72px${important}; height: 72px${important}; border-radius: 50%${important};
        background: linear-gradient(135deg, ${primaryGrad1}, ${primaryGrad2});
        display: flex${important}; align-items: center${important}; justify-content: center${important};
        animation: ttp-orb-breathe 1.4s ease-in-out infinite${important};
        box-shadow: 0 6px 24px ${primaryGrad1}66;
      }
      .ttp-wave { display: flex${important}; align-items: center${important}; gap: 3px${important}; height: 26px${important}; }
      .ttp-wave-bar {
        width: 2.5px${important}; border-radius: 3px${important}; background: rgba(255,255,255,0.9)${important};
        animation: ttp-wave 0.75s ease-in-out infinite alternate${important};
      }
      .ttp-listen-label { font-size: 12px${important}; color: rgba(255,255,255,0.4)${important}; }

      /* ── CONTROLS ── */
      .ttp-controls {
        padding: 16px 20px${important};
        display: flex${important}; align-items: center${important}; justify-content: center${important}; gap: 14px${important};
      }
      .ttp-ctrl-btn {
        width: 42px${important}; height: 42px${important}; border-radius: 50%${important};
        background: rgba(255,255,255,0.06)${important};
        border: 1px solid rgba(255,255,255,0.09)${important};
        cursor: pointer${important}; display: flex${important}; align-items: center${important}; justify-content: center${important};
        color: rgba(255,255,255,0.4)${important}; transition: background 0.2s${important};
        position: relative${important};
      }
      .ttp-ctrl-btn:hover { background: rgba(255,255,255,0.12)${important}; }
      .ttp-ctrl-btn.muted {
        background: rgba(239, 68, 68, 0.15)${important};
        border-color: rgba(239, 68, 68, 0.3)${important};
      }
      .ttp-ctrl-btn.paused {
        background: rgba(245, 158, 11, 0.15)${important};
        border-color: rgba(245, 158, 11, 0.3)${important};
      }
      .ttp-end-btn {
        width: 52px${important}; height: 52px${important}; border-radius: 50%${important};
        background: ${endCallColor}; border: none${important}; cursor: pointer${important};
        display: flex${important}; align-items: center${important}; justify-content: center${important};
        color: #fff${important}; transform: rotate(135deg)${important};
        box-shadow: 0 4px 16px ${endCallColor}59;
        transition: background 0.2s${important};
      }
      .ttp-end-btn:hover { background: ${endCallColor}; opacity: 0.85${important}; }

      /* Mute/speaker icon toggle */
      .desktop-mic-icon, .desktop-speaker-icon { display: block${important}; }
      .desktop-mic-off-icon, .desktop-speaker-off-icon {
        position: absolute${important}; top: 50%${important}; left: 50%${important};
        transform: translate(-50%, -50%)${important};
        width: 16px${important}; height: 16px${important}; z-index: 10${important};
      }
      /* Pause/play icon toggle */
      .desktop-pause-icon { display: block${important}; }
      .desktop-play-icon {
        position: absolute${important}; top: 50%${important}; left: 50%${important};
        transform: translate(-50%, -50%)${important};
        width: 16px${important}; height: 16px${important}; z-index: 10${important};
      }

      /* ── DESKTOP VOICE SECTION WRAPPER ── */
      .desktop-voice-section { flex-shrink: 0${important}; }

      /* ── COMPACT VOICE SECTION (shown when items/rooms are visible) ── */
      .compact-voice-section {
        padding: 10px 14px${important};
        background: transparent${important};
        display: flex${important};
        align-items: center${important};
        justify-content: space-between${important};
        flex-shrink: 0${important};
        border-bottom: 1px solid rgba(255,255,255,0.06)${important};
      }
      .compact-left { display: flex${important}; align-items: center${important}; gap: 10px${important}; }
      .compact-avatar {
        width: 42px${important}; height: 42px${important}; min-width: 42px${important};
        border-radius: 50%${important};
        background: linear-gradient(135deg, ${avatarGrad1}, ${avatarGrad2});
        display: flex${important}; align-items: center${important}; justify-content: center${important};
        flex-shrink: 0${important};
      }
      .compact-orb {
        width: 32px !important; height: 32px !important;
        border-radius: 50% !important;
      }
      .compact-info { display: flex${important}; flex-direction: column${important}; gap: 2px${important}; }
      .compact-timer {
        display: flex${important}; align-items: center${important}; gap: 5px${important};
        font-size: 11px${important}; font-weight: 600${important}; color: #e8e4f0${important};
      }
      .compact-status {
        font-size: 10px${important}; color: rgba(255,255,255,0.4)${important};
      }
      .compact-controls { display: flex${important}; align-items: center${important}; gap: 6px${important}; }
      .compact-control-btn {
        width: 36px${important}; height: 36px${important}; min-width: 36px${important};
        border-radius: 50%${important}; border: none${important};
        display: flex${important}; align-items: center${important}; justify-content: center${important};
        cursor: pointer${important}; transition: all 0.15s${important};
        position: relative${important};
      }
      .compact-control-btn svg { width: 16px${important}; height: 16px${important}; }
      .compact-control-btn .desktop-mic-off-icon,
      .compact-control-btn .desktop-speaker-off-icon {
        width: 16px${important}; height: 16px${important};
      }
      .compact-control-btn.secondary {
        background: rgba(255,255,255,0.08)${important}; color: rgba(255,255,255,0.5)${important};
      }
      .compact-control-btn.secondary:hover {
        background: rgba(255,255,255,0.14)${important}; color: rgba(255,255,255,0.7)${important};
      }
      .compact-control-btn.secondary.muted {
        background: rgba(239, 68, 68, 0.15)${important}; color: #ef4444${important};
      }
      .compact-control-btn.secondary.paused {
        background: rgba(245, 158, 11, 0.15)${important}; color: #f59e0b${important};
      }
      .compact-control-btn.secondary.muted .desktop-mic-icon,
      .compact-control-btn.secondary.muted .desktop-speaker-icon {
        stroke: #ef4444${important};
      }
      .compact-control-btn.secondary.muted .desktop-mic-off-icon,
      .compact-control-btn.secondary.muted .desktop-speaker-off-icon {
        width: 16px${important}; height: 16px${important};
      }
      .compact-control-btn.danger {
        width: 40px${important}; height: 40px${important}; min-width: 40px${important};
        background: #ef4444${important}; color: #fff${important};
        box-shadow: 0 3px 10px rgba(239,68,68,0.35)${important};
      }
      .compact-control-btn.danger:hover {
        transform: scale(1.05)${important};
        box-shadow: 0 4px 14px rgba(239,68,68,0.45)${important};
      }
      .compact-control-btn.danger svg { width: 18px${important}; height: 18px${important}; overflow: visible${important}; }

      /* ── TRANSCRIPT ── */
      .ttp-transcript {
        margin: 0 16px 16px${important};
        padding: 12px 14px${important};
        background: ${primaryGrad1}24;
        border: 1px solid ${primaryGrad1}4d;
        border-radius: 12px${important};
      }
      .ttp-transcript-hdr {
        display: flex${important}; align-items: center${important}; justify-content: space-between${important};
        margin-bottom: 7px${important};
      }
      .ttp-live-label {
        font-size: 10px${important}; color: ${primaryGrad2};
        text-transform: uppercase${important}; letter-spacing: 0.1em${important};
        font-weight: 600${important};
      }
      .ttp-toggle-text {
        font-size: 10px${important}; color: rgba(255,255,255,0.35)${important};
        cursor: pointer${important}; margin-left: auto${important}; margin-right: 8px${important};
        transition: color 0.2s${important};
      }
      .ttp-toggle-text:hover { color: ${primaryGrad2}; }
      .ttp-transcript-hdr { cursor: pointer${important}; }
      .ttp-dots { display: flex${important}; gap: 3px${important}; align-items: center${important}; }
      .ttp-dot {
        width: 3px${important}; height: 3px${important}; border-radius: 50%${important}; background: ${primaryGrad2};
        animation: ttp-pulse 0.8s ease-in-out infinite${important};
      }
      .ttp-dot:nth-child(2) { animation-delay: 0.2s${important}; }
      .ttp-dot:nth-child(3) { animation-delay: 0.4s${important}; }
      .ttp-transcript-text {
        font-size: 12px${important}; color: #e2e0f0${important}; line-height: 1.55${important};
        unicode-bidi: isolate${important};
        text-align: start${important};
      }
      .ttp-transcript-text .name { color: #c4b5fd${important}; font-weight: 600${important}; }
      .ttp-cursor {
        display: inline-block${important}; width: 2px${important}; height: 12px${important};
        background: ${primaryGrad2}; vertical-align: middle${important}; margin-inline-start: 2px${important};
        animation: ttp-blink 0.9s infinite${important};
      }

      /* Transcript collapsed/expanded state */
      .live-transcript-collapsed { display: block${important}; }
      .conversation-history { display: none${important}; }

      .ttp-transcript.expanded {
        flex: 1${important};
        display: flex${important};
        flex-direction: column${important};
        min-height: 0${important};
        overflow: hidden${important};
      }
      .ttp-transcript.expanded .live-transcript-collapsed { display: none${important}; }
      .ttp-transcript.expanded .conversation-history {
        display: flex${important};
        flex-direction: column${important};
        flex: 1${important};
        min-height: 0${important};
        overflow-y: auto${important};
        max-height: 260px${important};
        scrollbar-width: thin${important};
        scrollbar-color: ${primaryGrad1}4d transparent;
      }
      .ttp-transcript.expanded .conversation-history::-webkit-scrollbar { width: 4px${important}; }
      .ttp-transcript.expanded .conversation-history::-webkit-scrollbar-track { background: transparent${important}; }
      .ttp-transcript.expanded .conversation-history::-webkit-scrollbar-thumb {
        background: ${primaryGrad1}4d; border-radius: 2px${important};
      }

      /* Message rows in history */
      .conversation-messages { display: flex${important}; flex-direction: column${important}; gap: 8px${important}; padding-bottom: 4px${important}; }
      .message-row {
        display: flex${important}; align-items: flex-start${important}; gap: 8px${important};
        animation: ttp-msg-in 0.2s ease-out${important};
      }
      .message-row.user { flex-direction: row-reverse${important}; }
      /* Scoped to voice transcript rows only — avoids overriding standalone text-chat .message-bubble */
      .message-row .message-avatar {
        width: 26px${important}; height: 26px${important}; min-width: 26px${important};
        border-radius: 50%${important};
        display: flex${important}; align-items: center${important}; justify-content: center${important};
        font-size: 10px${important}; font-weight: 600${important}; flex-shrink: 0${important};
      }
      .message-row .message-avatar.user {
        background: rgba(255,255,255,0.12)${important}; color: rgba(255,255,255,0.6)${important};
      }
      .message-row .message-avatar.assistant {
        background: linear-gradient(135deg, ${avatarGrad1}, ${avatarGrad2});
      }
      .message-row .message-avatar .orb {
        width: 12px${important}; height: 12px${important}; border-radius: 50%${important};
        background: rgba(255,255,255,0.9)${important};
      }
      .message-row .message-bubble {
        padding: 8px 12px${important};
        border-radius: 12px${important};
        font-size: 12px${important}; line-height: 1.5${important};
        max-width: 80%${important}; word-wrap: break-word${important};
        unicode-bidi: plaintext${important};
        text-align: start${important};
      }
      .message-row.assistant .message-bubble {
        background: rgba(255,255,255,0.08)${important};
        color: #e2e0f0${important};
        border-bottom-left-radius: 4px${important};
      }
      .message-row.user .message-bubble {
        background: ${primaryGrad1}40;
        color: #e8e4f0${important};
        border-bottom-right-radius: 4px${important};
      }
      .live-message-row {
        display: flex${important}; align-items: flex-start${important}; gap: 8px${important};
        margin-top: 6px${important};
      }
      .live-message-row .message-bubble {
        position: relative${important};
        padding: 14px 12px 8px${important};
        border-radius: 12px${important};
        font-size: 12px${important}; line-height: 1.5${important};
        background: rgba(255,255,255,0.08)${important};
        color: #e2e0f0${important};
        max-width: 80%${important}; word-wrap: break-word${important};
      }
      .live-message-row .live-badge {
        position: absolute${important}; top: 0${important}; left: 10px${important};
        transform: translateY(-50%)${important};
        background: ${primaryGrad1}; color: #fff${important};
        font-size: 8px${important}; font-weight: 700${important};
        padding: 2px 6px${important}; border-radius: 4px${important};
        text-transform: uppercase${important}; letter-spacing: 0.05em${important};
      }
      .typing-cursor {
        display: inline-block${important}; width: 2px${important}; height: 12px${important};
        background: ${primaryGrad2}; vertical-align: middle${important}; margin-inline-start: 2px${important};
        animation: ttp-blink 0.9s infinite${important};
      }
      @keyframes ttp-msg-in {
        from { opacity: 0; transform: translateY(4px); }
        to { opacity: 1; transform: translateY(0); }
      }

      /* ── TYPE ROW ── */
      .ttp-type-row { display: flex${important}; gap: 8px${important}; padding: 0 16px 16px${important}; flex-wrap: wrap${important}; }
      .ttp-input {
        flex: 1${important}; padding: 9px 12px${important};
        background: rgba(255,255,255,0.05)${important};
        border: 1px solid rgba(255,255,255,0.09)${important};
        border-radius: 10px${important}; color: rgba(255,255,255,0.6)${important};
        font-size: 12px${important}; outline: none${important}; font-family: inherit${important};
        transition: border-color 0.2s${important};
        text-align: start${important};
      }
      .ttp-input::placeholder { color: rgba(255,255,255,0.25)${important}; }
      .ttp-input:focus { border-color: ${primaryGrad1}80; }
      .ttp-send-btn {
        width: 34px${important}; height: 34px${important}; border-radius: 9px${important};
        background: ${sendBtnColor}; border: none${important}; cursor: pointer${important};
        display: flex${important}; align-items: center${important}; justify-content: center${important};
        flex-shrink: 0${important}; transition: background 0.2s${important};
      }
      .ttp-send-btn:hover { background: ${sendBtnColor}; opacity: 0.85${important}; }

      /* ── KEYFRAMES ── */
      @keyframes ttp-pulse  { 0%,100%{opacity:1} 50%{opacity:.3} }
      @keyframes ttp-blink  { 0%,49%{opacity:1} 50%,100%{opacity:0} }
      @keyframes ttp-ring   { 0%,100%{transform:scale(1);opacity:.6} 50%{transform:scale(1.22);opacity:0} }
      @keyframes ttp-orb-breathe { 0%,100%{transform:scale(1)} 50%{transform:scale(1.05)} }
      @keyframes ttp-wave   { from{transform:scaleY(.15);opacity:.3} to{transform:scaleY(1);opacity:.9} }
      @keyframes ttp-pill-pulse { 0%,100%{box-shadow:0 0 0 0 ${primaryGrad1}80} 60%{box-shadow:0 0 0 7px ${primaryGrad1}00} }

      /* Mobile Voice Call UI - Shared styles (also used in generateMobileCSS) */
      ${this._getSharedMobileCSS(important)}

    `;
  }

  /**
   * Get shared mobile CSS used by both generateCSS and generateMobileCSS
   * @private
   */
  _getSharedMobileCSS(important) {
    const c = this.config;
    const avatarGrad1 = c.avatarGradient1 || '#6d56f5';
    const avatarGrad2 = c.avatarGradient2 || '#a78bfa';
    /* Match .ttp-mobile-landing / mobile FAB (TTPChatWidget pillGradient) — not desktop hero/call surface */
    const mobileLandingSurface = c.pillGradient || 'linear-gradient(135deg, #581c87, #312e81, #1e1b4b)';
    /* Primary = Start Call CTA — user bubbles + send only */
    const primaryGrad1 = c.primaryBtnGradient1 || c.startCallButtonColor || '#6d56f5';
    const primaryGrad2 = c.primaryBtnGradient2 || c.startCallButtonColor || '#9d8df8';
    const sendBtnColor = c.sendButtonColor || primaryGrad1;
    const endCallColor = c.endCallButtonColor || '#ef4444';
    const mobileAccentGradient = `linear-gradient(135deg, ${sendBtnColor}, ${primaryGrad2})`;
    return `
      /* Mobile voice idle: optional header image replaces 🤖 */
      .mobile-voice-interface #voiceIdleState .voice-avatar {
        width: 80px${important};
        height: 80px${important};
        margin: 12px auto 16px${important};
        border-radius: 50%${important};
        background: linear-gradient(135deg, ${avatarGrad1}, ${avatarGrad2})${important};
        display: flex${important};
        align-items: center${important};
        justify-content: center${important};
        font-size: 40px${important};
        line-height: 1${important};
        flex-shrink: 0${important};
      }
      .mobile-voice-interface #voiceIdleState .voice-avatar.voice-avatar--image {
        padding: 0${important};
        overflow: hidden${important};
      }
      .mobile-voice-interface #voiceIdleState .voice-avatar-img {
        width: 100%${important};
        height: 100%${important};
        object-fit: cover${important};
        border-radius: 50%${important};
        display: block${important};
      }

      /* Idle hero copy: light text on dark / gradient panels (host themes often force dark body color) */
      .mobile-voice-interface #voiceIdleState .voice-status {
        text-align: center${important};
      }
      .mobile-voice-interface #voiceIdleState .voice-status-title {
        color: rgba(255,255,255,0.96)${important};
      }
      .mobile-voice-interface #voiceIdleState .voice-status-subtitle {
        color: rgba(255,255,255,0.78)${important};
      }

      .mobile-duration-dot {
display: block !important;
                width: 6px${important};
        height: 6px${important};
        border-radius: 50%${important};
        background-color: #ef4444${important};
        animation: mobilePulse 1.5s ease-in-out infinite${important};
      }

      @keyframes mobilePulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.5; transform: scale(0.95); }
      }

      .mobile-voice-bar {
        display: flex${important};
        flex-direction: column${important};
        gap: 10px${important};
        padding: 14px${important};
        position: relative${important};
        background: ${mobileLandingSurface}${important};
        border-radius: 24px 24px 0 0${important};
        box-shadow:
          0 -8px 32px rgba(0, 0, 0, 0.3),
          0 -2px 8px rgba(0, 0, 0, 0.35),
          inset 0 1px 0 rgba(255, 255, 255, 0.1)${important};
        width: 100% !important;
        max-width: 100% !important;
        pointer-events: auto${important};
        overflow: hidden !important;
        margin: 0 !important;
      }

      .mobile-top-row {
        display: flex${important};
        align-items: center${important};
        justify-content: flex-start${important};
        gap: 8px${important};
        position: relative${important};
        width: 100%${important};
      }

      .mobile-status-indicator {
        display: flex${important};
        align-items: center${important};
        gap: 5px${important};
        min-width: 85px${important};
        flex: 0 0 auto${important};
      }

      .mobile-status-dot {
display: block !important;
                width: 7px${important};
        height: 7px${important};
        border-radius: 50%${important};
        background-color: #22c55e${important};
        box-shadow: 0 0 8px #22c55e${important};
        transition: all 0.3s ease${important};
      }

      .mobile-status-text {
        font-size: 11px${important};
        font-weight: 700${important};
        color: rgba(255,255,255,0.95)${important};
        text-transform: uppercase${important};
        letter-spacing: 0.3px${important};
      }

      .mobile-waveform-center {
        position: relative !important;
        flex: 0 0 auto !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        margin-left: 10px !important;
        height: 26px !important;
        max-height: 26px !important;
        max-width: 76px !important;
        overflow: hidden !important;
        visibility: visible !important;
      }

      .mobile-waveform-bars {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        gap: 3px !important;
        max-height: 26px !important;
        overflow: hidden !important;
      }

      .mobile-waveform-bar {
        display: block !important;
        width: 3px !important;
        min-height: 4px !important;
        max-height: 22px !important;
        background-color: #fff !important;
        border-radius: 2px !important;
        transition: height 0.12s ease-out !important;
        box-shadow: 0 0 6px rgba(255,255,255,0.5) !important;
        visibility: visible !important;
        align-self: center !important;
      }

      .mobile-controls {
        display: flex${important};
        align-items: center${important};
        gap: 6px${important};
        margin-left: auto${important};
        flex-shrink: 0${important};
        pointer-events: auto${important};
        touch-action: manipulation${important};
        position: relative${important};
        z-index: 120${important};
      }

      .mobile-control-btn {
        display: flex${important};
        align-items: center${important};
        justify-content: center${important};
        width: 32px${important};
        height: 32px${important};
        min-width: 32px${important};
        min-height: 32px${important};
        max-width: 32px${important};
        max-height: 32px${important};
        border-radius: 50%${important};
        border: none${important};
        cursor: pointer${important};
        transition: all 0.2s ease${important};
        background-color: rgba(255,255,255,0.15)${important};
        position: relative${important};
        overflow: hidden${important};
        padding: 0${important};
        margin: 0${important};
        box-sizing: border-box${important};
      }

      .mobile-control-btn:hover {
        background-color: rgba(255,255,255,0.25)${important};
      }

      .mobile-control-btn.muted {
        background-color: rgba(239, 68, 68, 0.3)${important};
      }
      .mobile-control-btn.paused {
        background-color: rgba(245, 158, 11, 0.3)${important};
      }

      .mobile-control-btn .mobile-mic-icon,
      .mobile-control-btn .mobile-speaker-icon {
        position: absolute${important};
        top: 50%${important};
        left: 50%${important};
        transform: translate(-50%, -50%)${important};
        display: block${important};
        visibility: visible${important};
        opacity: 1${important};
        width: 15px${important};
        height: 15px${important};
        margin: 0${important};
        padding: 0${important};
      }

      .mobile-control-btn .mobile-mic-off-icon,
      .mobile-control-btn .mobile-speaker-off-icon {
        position: absolute${important};
        top: 50%${important};
        left: 50%${important};
        transform: translate(-50%, -50%)${important};
        width: 15px${important};
        height: 15px${important};
        margin: 0${important};
        padding: 0${important};
      }

      .mobile-control-btn:not(.muted) .mobile-mic-off-icon,
      .mobile-control-btn:not(.muted) .mobile-speaker-off-icon {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
      }

      .mobile-control-btn.muted .mobile-mic-icon,
      .mobile-control-btn.muted .mobile-speaker-icon {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
      }

      .mobile-control-btn.muted .mobile-mic-off-icon,
      .mobile-control-btn.muted .mobile-speaker-off-icon {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
      }

      .mobile-end-call-btn {
        display: flex${important};
        align-items: center${important};
        justify-content: center${important};
        width: 36px${important};
        height: 36px${important};
        min-width: 36px${important};
        min-height: 36px${important};
        max-width: 36px${important};
        max-height: 36px${important};
        border-radius: 50%${important};
        border: none${important};
        cursor: pointer${important};
        background-color: ${endCallColor}${important};
        box-shadow: 0 4px 14px ${endCallColor}80${important};
        transition: all 0.2s ease${important};
        overflow: hidden${important};
        padding: 0${important};
        margin: 0${important};
        box-sizing: border-box${important};
      }

      .mobile-end-call-btn svg {
        display: block${important};
        visibility: visible${important};
        opacity: 1${important};
        width: 15px${important};
        height: 15px${important};
        margin: 0${important};
        padding: 0${important};
        position: relative${important};
      }

      /* ID-specific selectors for WordPress/Shopify compatibility */
      #mobileMuteBtn.mobile-control-btn,
      #mobilePauseBtn.mobile-control-btn,
      #mobileSpeakerBtn.mobile-control-btn,
      #mobileEndCallBtn.mobile-end-call-btn {
        border-radius: 50% !important;
        overflow: hidden !important;
        pointer-events: auto !important;
        touch-action: manipulation !important;
        -webkit-appearance: none !important;
        appearance: none !important;
        text-decoration: none !important;
        line-height: 1 !important;
        font-size: 0 !important;
        letter-spacing: 0 !important;
        text-indent: 0 !important;
        text-transform: none !important;
        color: transparent !important;
      }

      #mobileMuteBtn.mobile-control-btn,
      #mobilePauseBtn.mobile-control-btn,
      #mobileSpeakerBtn.mobile-control-btn {
        width: 32px !important;
        height: 32px !important;
        min-width: 32px !important;
        min-height: 32px !important;
        max-width: 32px !important;
        max-height: 32px !important;
      }

      #mobileEndCallBtn.mobile-end-call-btn {
        width: 36px !important;
        height: 36px !important;
        min-width: 36px !important;
        min-height: 36px !important;
        max-width: 36px !important;
        max-height: 36px !important;
      }

      #mobileMuteBtn.mobile-control-btn .mobile-mic-icon,
      #mobileMuteBtn.mobile-control-btn .mobile-mic-off-icon,
      #mobilePauseBtn.mobile-control-btn .mobile-pause-icon,
      #mobilePauseBtn.mobile-control-btn .mobile-play-icon,
      #mobileSpeakerBtn.mobile-control-btn .mobile-speaker-icon,
      #mobileSpeakerBtn.mobile-control-btn .mobile-speaker-off-icon,
      #mobileEndCallBtn.mobile-end-call-btn svg {
        width: 15px !important;
        height: 15px !important;
        max-width: 15px !important;
        max-height: 15px !important;
        margin: 0 !important;
        padding: 0 !important;
        pointer-events: none !important;
        overflow: visible !important;
      }

      #mobile-voice-call-bar-container svg path,
      #mobile-voice-call-bar-container svg line,
      #mobile-voice-call-bar-container svg polygon,
      #mobile-voice-call-bar-container svg circle {
        pointer-events: none !important;
        vector-effect: none !important;
      }

      .mobile-end-call-btn:hover {
        transform: scale(1.05)${important};
      }

      .mobile-transcript-row {
        width: 100%${important};
        display: flex${important};
        flex-direction: column${important};
        gap: 6px${important};
        padding: 10px 12px${important};
        background: rgba(255,255,255,0.18)${important};
        border-radius: 14px${important};
        border: none${important};
        cursor: pointer${important};
        text-align: start${important};
        backdrop-filter: blur(4px)${important};
        transition: background 0.2s ease${important};
        pointer-events: auto !important;
        position: relative !important;
        z-index: 100 !important;
      }

      .mobile-transcript-row:hover {
        background: rgba(255,255,255,0.25)${important};
      }

      .mobile-transcript-text {
        margin: 0${important};
        font-size: 13px${important};
        color: #fff${important};
        line-height: 1.4${important};
        font-weight: 500${important};
        unicode-bidi: plaintext${important};
        text-align: start${important};
        display: -webkit-box !important;
        -webkit-line-clamp: 2 !important;
        -webkit-box-orient: vertical !important;
        overflow: hidden !important;
        min-height: 2.8em !important;
      }

      .mobile-transcript-footer {
        display: flex${important};
        align-items: center${important};
        justify-content: space-between${important};
      }

      .mobile-expand-hint {
        font-size: 10px${important};
        color: rgba(255,255,255,0.6)${important};
        text-transform: uppercase${important};
        letter-spacing: 0.5px${important};
        font-weight: 600${important};
      }

      /* Base: hidden. Inline display:none loses to display:flex!important — use modifier for open state */
      .mobile-transcript-overlay {
        position: fixed${important};
        top: 0${important};
        left: 0${important};
        right: 0${important};
        bottom: 0${important};
        background-color: rgba(0,0,0,0.6)${important};
        display: none${important};
        padding: 12px${important};
        z-index: 2147483647${important};
        pointer-events: auto${important};
      }

      .mobile-transcript-overlay.mobile-transcript-overlay--open {
        display: flex${important};
        align-items: flex-end${important};
      }

      .mobile-expanded-transcript {
        width: 100%${important};
        max-height: 75%${important};
        background: #fff${important};
        border-radius: 24px 24px 16px 16px${important};
        overflow: hidden${important};
        box-shadow: 0 -8px 40px rgba(0,0,0,0.3)${important};
        display: flex${important};
        flex-direction: column${important};
      }

      .mobile-transcript-header {
        display: flex${important};
        justify-content: space-between${important};
        align-items: center${important};
        padding: 14px 16px${important};
        background: ${mobileLandingSurface}${important};
        flex-shrink: 0${important};
      }

      .mobile-header-left {
        display: flex${important};
        flex-direction: column${important};
        gap: 2px${important};
      }

      .mobile-header-status {
        display: flex${important};
        align-items: center${important};
        gap: 6px${important};
      }

      .mobile-header-status-dot {
display: block !important;
                width: 6px${important};
        height: 6px${important};
        border-radius: 50%${important};
        background-color: #22c55e${important};
        box-shadow: 0 0 6px #22c55e${important};
      }

      .mobile-header-status-text {
        font-size: 10px${important};
        font-weight: 700${important};
        color: rgba(255,255,255,0.9)${important};
        text-transform: uppercase${important};
        letter-spacing: 0.5px${important};
      }

      .mobile-header-duration {
        font-size: 10px${important};
        font-weight: 600${important};
        color: rgba(255,255,255,0.7)${important};
        margin-left: 8px${important};
      }

      .mobile-transcript-label {
        font-size: 11px${important};
        font-weight: 800${important};
        color: rgba(255,255,255,0.7)${important};
        letter-spacing: 1px${important};
      }

      .mobile-close-transcript-btn {
        display: flex${important};
        align-items: center${important};
        justify-content: center${important};
        width: 32px${important};
        height: 32px${important};
        border-radius: 50%${important};
        border: none${important};
        background: rgba(255,255,255,0.2)${important};
        color: #fff${important};
        cursor: pointer${important};
      }

      .mobile-close-transcript-btn:hover {
        background: rgba(255,255,255,0.3)${important};
      }

      .mobile-messages-container {
        flex: 1${important};
        padding: 16px${important};
        overflow-y: auto${important};
        display: flex${important};
        flex-direction: column${important};
        gap: 12px${important};
      }

      .mobile-message-bubble {
        max-width: 85%${important};
        padding: 12px 16px${important};
        border-radius: 18px${important};
        font-size: 14px${important};
        line-height: 1.5${important};
        word-wrap: break-word${important};
        unicode-bidi: plaintext${important};
        text-align: start${important};
      }

      .mobile-message-bubble.assistant {
        align-self: flex-start${important};
        background-color: #f3f4f6${important};
        color: #374151${important};
        border-bottom-left-radius: 4px${important};
      }

      .mobile-message-bubble.user {
        align-self: flex-end${important};
        background: ${mobileAccentGradient}${important};
        color: #fff${important};
        border-bottom-right-radius: 4px${important};
      }

      .mobile-input-area {
        padding: 12px 16px 16px${important};
        border-top: 1px solid #e5e7eb${important};
        background: #f9fafb${important};
        flex-shrink: 0${important};
      }

      .mobile-input-wrapper {
        display: flex${important};
        align-items: center${important};
        gap: 8px${important};
        padding: 4px 4px 4px 16px${important};
        background: #fff${important};
        border-radius: 24px${important};
        border: 2px solid #e5e7eb${important};
      }

      .mobile-text-input {
        flex: 1${important};
        border: none${important};
        outline: none${important};
        font-size: 14px${important};
        color: #374151${important};
        background: transparent${important};
        padding: 10px 0${important};
        text-align: start${important};
      }

      .mobile-text-input::placeholder {
        color: #9ca3af${important};
      }

      .mobile-send-btn {
        display: flex${important};
        align-items: center${important};
        justify-content: center${important};
        width: 40px${important};
        height: 40px${important};
        border-radius: 50%${important};
        border: none${important};
        background: ${mobileAccentGradient}${important};
        color: #fff${important};
        cursor: pointer${important};
        transition: all 0.2s ease${important};
      }

      .mobile-send-btn:hover:not(:disabled) {
        transform: scale(1.05)${important};
      }

      .mobile-send-btn:disabled {
        opacity: 0.5${important};
        cursor: not-allowed${important};
      }

      .mobile-input-hint {
        margin: 8px 0 0${important};
        font-size: 11px${important};
        color: #9ca3af${important};
        text-align: center${important};
        transition: color 0.2s ease${important};
      }

      /* Mobile text input status states */
      .mobile-input-hint--sending {
        color: #666${important};
      }

      .mobile-input-hint--success {
        color: #28a745${important};
      }

      .mobile-input-hint--queued {
        color: #ffc107${important};
      }

      .mobile-input-hint--error {
        color: #dc3545${important};
      }

      /* Disabled mobile input styling */
      #mobileTextInput:disabled {
        background-color: #f5f5f5${important};
        cursor: not-allowed${important};
      }

      #mobileSendBtn:disabled {
        opacity: 0.5${important};
        cursor: not-allowed${important};
      }
    `;
  }

  /**
   * Generate mobile-specific CSS for minimized bar (injected into document.head).
   * Always uses !important since the mobile bar lives in document.body, never in shadow DOM.
   */
  generateMobileCSS() {
    const important = ' !important';

    return `
      /* Mobile Voice Call UI - Container positioning */
      #mobile-voice-call-bar-container {
        position: fixed !important;
        bottom: 0 !important;
        left: 0 !important;
        right: 0 !important;
        z-index: 99999 !important;
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif !important;
        padding: 0 12px 12px !important;
        pointer-events: none${important};
        box-sizing: border-box !important;
        width: 100% !important;
        max-width: 100vw !important;
        margin: 0 !important;
        overflow: visible !important;
        isolation: isolate !important;
      }

      #mobile-voice-call-bar-container *,
      #mobile-voice-call-bar-container *::before,
      #mobile-voice-call-bar-container *::after {
        box-sizing: border-box !important;
      }

      /* Mobile loading: small spinner in waveform area until first audio */
      .mobile-waveform-spinner {
        position: absolute !important;
        left: 50% !important;
        top: 50% !important;
        transform: translate(-50%, -50%) !important;
        width: 16px !important;
        height: 16px !important;
      }

      .mobile-waveform-spinner-inner {
        width: 100% !important;
        height: 100% !important;
        border: 2px solid rgba(255,255,255,0.3) !important;
        border-top-color: #fff !important;
        border-radius: 50% !important;
        animation: mobileLoadingSpin 0.8s linear infinite !important;
      }

      .mobile-waveform-spinner--hidden {
        display: none !important;
      }

      @keyframes mobileLoadingSpin {
        to { transform: rotate(360deg); }
      }

      .mobile-waveform-bars--hidden {
        visibility: hidden !important;
        opacity: 0 !important;
      }

      .mobile-duration-badge--hidden {
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }

      .mobile-duration-badge {
        position: absolute !important;
        top: -28px !important;
        left: 50% !important;
        transform: translateX(-50%) !important;
        display: flex !important;
        align-items: center !important;
        gap: 6px !important;
        padding: 5px 14px !important;
        background: rgba(0,0,0,0.75) !important;
        border-radius: 20px !important;
        font-size: 12px !important;
        font-weight: 700 !important;
        color: #fff !important;
        backdrop-filter: blur(8px) !important;
        -webkit-backdrop-filter: blur(8px) !important;
        z-index: 10 !important;
        pointer-events: auto !important;
        visibility: visible !important;
        opacity: 1 !important;
      }

      /* Fly-to-cart target: invisible anchor for animation when cart row is hidden */
      .ttp-fly-target {
        position: absolute${important};
        bottom: 14px${important};
        left: 30px${important};
        width: 24px${important};
        height: 24px${important};
        pointer-events: none${important};
      }

      /* ── Mobile Cart Row ── */

      .mobile-cart-row {
        display: flex${important};
        align-items: center${important};
        justify-content: space-between${important};
        padding: 7px 16px${important};
        background: rgba(255, 255, 255, 0.08)${important};
        border-top: 1px solid rgba(255, 255, 255, 0.1)${important};
        border-radius: 0 0 16px 16px${important};
        animation: mobileCartRowIn 0.25s ease-out${important};
        pointer-events: auto${important};
      }

      @keyframes mobileCartRowIn {
        from { opacity: 0; max-height: 0; padding-top: 0; padding-bottom: 0; }
        to   { opacity: 1; max-height: 50px; padding-top: 7px; padding-bottom: 7px; }
      }

      .mobile-cart-row__content {
        display: flex${important};
        align-items: center${important};
        gap: 8px${important};
      }

      .mobile-cart-row__icon {
        font-size: 13px${important};
      }

      .mobile-cart-row__text {
        font-size: 12px${important};
        font-weight: 600${important};
        color: rgba(255, 255, 255, 0.9)${important};
        letter-spacing: 0.2px${important};
      }

      /* Shared mobile styles */
      ${this._getSharedMobileCSS(important)}
    `;
  }
}
